import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { authApi, settingsApi } from '../../services/api';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import { useScrollToError } from '../../hooks/useScrollToError';
import './Login.css';

function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const errorRef = useScrollToError(error);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotUsername, setForgotUsername] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const [trainingMode, setTrainingMode] = useState(false);

  useEffect(() => {
    settingsApi.getTrainingMode()
      .then(res => setTrainingMode(res.data.enabled))
      .catch(() => {});
  }, []);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err: any) {
      setError(err.message || t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMessage('');
    setError('');

    try {
      const res = await authApi.forgotPassword({ email: forgotEmail, username: forgotUsername });
      setForgotMessage(res.data.message || t('auth.credentialsMatch'));
    } catch (err: any) {
      if (err.response?.status === 429) {
        setError(t('auth.pleaseWait'));
      } else {
        setError(err.response?.data?.error || t('auth.requestFailed'));
      }
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className={`login-page${trainingMode ? ' login-training-mode' : ''}`}>
      <div className="login-branding">
        <img
          src="logo_arcostxt.png"
          alt="Logo ARCOS"
          className="login-brand-logo"
        />
      </div>

      {trainingMode && (
        <div className="training-mode-banner">
          {t('auth.trainingMode')}
        </div>
      )}

      <div className="login-container" style={{ position: 'relative' }}>
        <LoadingOverlay loading={loading || forgotLoading} />
        {!showForgotPassword ? (
          <>
            <form onSubmit={handleSubmit} className="login-form">
              {error && <div ref={errorRef} className="error">{error}</div>}

              <div className="form-group">
                <label htmlFor="email">{t('auth.email')}</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.enterEmail')}
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">{t('auth.password')}</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.enterPassword')}
                  required
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-block"
                disabled={loading}
              >
                {loading ? t('auth.loggingIn') : t('auth.login')}
              </button>
            </form>

            <div className="login-footer">
              <p>
                <button
                  type="button"
                  className="forgot-password-link"
                  onClick={() => { setShowForgotPassword(true); setError(''); }}
                >
                  {t('auth.forgotPassword')}
                </button>
              </p>
              <p>{t('auth.forAccess')}</p>
            </div>
          </>
        ) : (
          <>
            <form onSubmit={handleForgotPassword} className="login-form">
              <h3 style={{ margin: `0 0 var(--space-2) 0`, fontSize: 'var(--font-size-lg)', color: 'var(--color-primary)' }}>{t('auth.forgotPasswordTitle')}</h3>
              <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', margin: `0 0 var(--space-4) 0` }}>
                {t('auth.forgotPasswordDesc')}
              </p>

              {error && <div ref={errorRef} className="error">{error}</div>}
              {forgotMessage && <div className="success">{forgotMessage}</div>}

              <div className="form-group">
                <label htmlFor="forgot-username">{t('auth.username')}</label>
                <input
                  id="forgot-username"
                  type="text"
                  value={forgotUsername}
                  onChange={(e) => setForgotUsername(e.target.value)}
                  placeholder={t('auth.enterUsername')}
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="forgot-email">{t('auth.email')}</label>
                <input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder={t('auth.enterEmail')}
                  required
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-block"
                disabled={forgotLoading || !!forgotMessage}
              >
                {forgotLoading ? t('auth.sending') : t('auth.requestPasswordReset')}
              </button>

              <button
                type="button"
                className="btn btn-outline btn-block"
                style={{ marginTop: 'var(--space-2)' }}
                onClick={() => { setShowForgotPassword(false); setForgotMessage(''); setError(''); }}
              >
                {t('auth.backToLogin')}
              </button>
            </form>
          </>
        )}
      </div>

      <div className="login-branding">
        <img
          src="/Logo_DHC WS.png"
          alt="Logo DHC"
          className="login-brand-logo-dhc"
        />
        <p className="login-dedication">{t('auth.dedication')}</p>
      </div>

    </div>
  );
}

export default Login;
